import { apiCreateOrder } from '../lib/api';
import { useCartStore } from '../store/cartStore';
import type { Product } from '../types';
import { useAuth } from './useAuth';
import { useToast } from './useToast';

export function useCart() {
  const store = useCartStore();
  const { isAuthenticated } = useAuth();
  const { success, error } = useToast();

  const addToCart = (product: Product) => {
    const item = store.items.find((current) => current.productId === product.id);
    if (item) {
      store.updateQuantity(product.id, item.quantity + 1);
    } else {
      store.addItem({
        productId: product.id,
        name: product.name,
        image: product.image,
        price: product.price
      });
    }
    success(item ? 'Cantidad actualizada' : 'Producto agregado al carrito');
  };

  const checkout = async (options?: {
    priorityDelivery?: boolean;
    address?: string;
    city?: string;
    phone?: string;
    notes?: string;
  }) => {
    if (!isAuthenticated || store.items.length === 0) {
      error(!isAuthenticated ? 'Debes iniciar sesión para completar tu compra' : 'El carrito está vacío');
      return null;
    }

    try {
      const order = await apiCreateOrder({
        items: store.items,
        // Delivery and total are deliberately recomputed by the Worker.
        priorityDelivery: options?.priorityDelivery === true,
        address: options?.address || null,
        city: options?.city || null,
        phone: options?.phone ? options.phone.replace(/\D/g, '') : null,
        notes: options?.notes || null,
      });
      success('Orden creada exitosamente');
      return order;
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : '';
      let detail = '';
      try {
        const parsed = JSON.parse(message);
        detail = typeof parsed.error === 'string' ? parsed.error : '';
      } catch {
        detail = message;
      }
      error(detail && detail !== 'Internal server error' ? detail : 'Error al crear la orden');
      return null;
    }
  };

  return { ...store, addToCart, checkout };
}
